import React, { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Coluna, ColunaHeader, FiltrosResultado, DropZone, EmptyState } from './styles';
import CardPendencia from './CardPendencia';
import { PendenciaKanban, ColunaKanban, ResultadoType, STATUS_CONCLUIDAS } from './types';

interface ColunaPendenciasProps {
    coluna: ColunaKanban;
    pendencias: PendenciaKanban[];
    isOver: boolean;
    onFilterResultado?: (resultado: ResultadoType) => void;
    filtroAtivo?: ResultadoType;
    onView?: (pendencia: PendenciaKanban) => void;
    totalConcluidasSemFiltro?: number;
    todasPendenciasConcluidas?: PendenciaKanban[];
    userProfile?: string;
    userId?: number | null;
    idEscritorioSolicitante?: number | null;
}

const ColunaPendencias: React.FC<ColunaPendenciasProps> = ({
    coluna,
    pendencias,
    isOver,
    onFilterResultado,
    filtroAtivo,
    onView,
    totalConcluidasSemFiltro,
    todasPendenciasConcluidas,
    userProfile,
    userId,
    idEscritorioSolicitante
}) => {
    const dropZoneDroppable = useDroppable({
        id: coluna.id,
    });

    const Icon = coluna.icon;
    const isConcluidas = coluna.id === STATUS_CONCLUIDAS;

    const contadoresResultado = useMemo(() => {
        const pendenciasParaContar = isConcluidas && todasPendenciasConcluidas 
            ? todasPendenciasConcluidas 
            : pendencias;
        
        return {
            resolvidas: pendenciasParaContar.filter(p => p.resultado === 'Resolvida').length,
            aceitas: pendenciasParaContar.filter(p => p.resultado === 'Aceita').length,
            recusadas: pendenciasParaContar.filter(p => p.resultado === 'Recusada').length,
        };
    }, [pendencias, todasPendenciasConcluidas, isConcluidas]);

    const sortableItems = useMemo(() => 
        pendencias.map(p => p.id_pendencia.toString()),
        [pendencias]
    );

    return (
        <Coluna isOver={isOver} bgColor={coluna.bgColor}>
            <ColunaHeader bgColor={coluna.bgColor}>
                <div className="header-content">
                    <div className="title-group">
                        <Icon size={16} color={coluna.iconColor} />
                        <h3>{coluna.title}</h3>
                        {isConcluidas && (totalConcluidasSemFiltro ?? pendencias.length) > 0 && (
                            <FiltrosResultado>
                                <button
                                    className={`resolvida ${filtroAtivo === 'Resolvida' ? 'ativo' : ''}`}
                                    onClick={() => onFilterResultado?.('Resolvida')}
                                >
                                    Resolvidas {contadoresResultado.resolvidas}
                                </button>
                                <button
                                    className={`aceita ${filtroAtivo === 'Aceita' ? 'ativo' : ''}`}
                                    onClick={() => onFilterResultado?.('Aceita')}
                                >
                                    Aceitas {contadoresResultado.aceitas}
                                </button>
                                <button
                                    className={`recusada ${filtroAtivo === 'Recusada' ? 'ativo' : ''}`}
                                    onClick={() => onFilterResultado?.('Recusada')}
                                >
                                    Recusadas {contadoresResultado.recusadas}
                                </button>
                            </FiltrosResultado>
                        )}
                    </div>
                    <div className="badge">
                        {isConcluidas ? (totalConcluidasSemFiltro ?? pendencias.length) : pendencias.length}
                    </div>
                </div>
            </ColunaHeader>

            <DropZone ref={dropZoneDroppable.setNodeRef} isOver={isOver}>
                {pendencias.length > 0 ? (
                    <SortableContext 
                        items={sortableItems} 
                        strategy={verticalListSortingStrategy}
                    >
                        {pendencias.map((pendencia) => (
                            <CardPendencia
                                key={pendencia.id_pendencia}
                                pendencia={pendencia}
                                onView={onView}
                                userProfile={userProfile}
                                userId={userId}
                                idEscritorioSolicitante={idEscritorioSolicitante}
                            />
                        ))}
                    </SortableContext>
                ) : (
                    <EmptyState isOver={isOver}>
                        {isOver ? '✓ Solte aqui' : 'Nenhuma pendência'}
                    </EmptyState>
                )}
            </DropZone>
        </Coluna>
    );
};

export default ColunaPendencias;

